import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Ref } from 'vue'
import type { AvatarPathPoint } from '@/customTypes'

import Logger from '@/services/Logger.js'

const logger: any = new Logger('ProgressiveMapStore', true)

interface FlipArea {
  id: string
  x: number
  y: number
  width: number
  height: number
}

export const useProgressiveMapStore = defineStore('progressiveMap', () => {
  const avatarCurrentPath: Ref<AvatarPathPoint[]> = ref([])
  const rewardSceneSvgDimensions = ref({
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    viewBox: [0, 0, 1920, 1080]
  })

  const progressiveMapReference = ref<SVGSVGElement | null>(null)
  const pathSegmentReferences = ref<Record<string, SVGPathElement>>({})
  const flipZoneReference = ref<SVGElement | null>(null)

  const initializeMapComponents = (
    map: SVGSVGElement | null,
    path: Record<string, SVGPathElement>,
    flipZone: SVGElement | null
  ) => {
    progressiveMapReference.value = map
    pathSegmentReferences.value = path
    flipZoneReference.value = flipZone
  }

  // Generate path points along the path segment to animate between.
  const generatePathPoints = (
    pathSegmentReferences: SVGPathElement,
    baseDensity: number = 0.02 // higher density will result in smoother path, but more points to walk between and heavier on performance
  ): AvatarPathPoint[] => {
    if (!pathSegmentReferences) return []
    const points: AvatarPathPoint[] = []
    const pathLength = pathSegmentReferences.getTotalLength()

    const numPoints = Math.max(Math.ceil(pathLength * baseDensity), 2)

    // Generate points evenly spaced along the full path
    for (let i = 0; i < numPoints; i++) {
      const distance = (pathLength / (numPoints - 1)) * i
      const point = pathSegmentReferences.getPointAtLength(distance)
      points.push({ x: point.x, y: point.y })
    }

    return points
  }

  const getAvatarPathSegment = (
    startIndex: number,
    endIndex: number,
    isFinished: boolean = false
  ) => {
    initializeFlipAreas()
    if (!pathSegmentReferences.value) return null
    const currentPath = pathSegmentReferences.value[`path-${startIndex}-${endIndex}`]

    // Generate path points between the waypoints if not already generated
    if (avatarCurrentPath.value.length === 0) {
      if (!currentPath) {
        logger.error('Avatar path element not found')
        return null
      }

      if (!isFinished) {
        avatarCurrentPath.value = generatePathPoints(currentPath)
      }
    }

    return avatarCurrentPath.value?.map(convertToScreenCoordinates) || []
  }

  const getAvatarPathEndpoint = () => {
    return convertToScreenCoordinates(avatarCurrentPath.value[avatarCurrentPath.value.length - 1])
  }

  const updateRewardSceneDimensions = () => {
    if (!progressiveMapReference.value) return
    const rect = progressiveMapReference.value.getBoundingClientRect()
    const viewBox = progressiveMapReference.value
      .getAttribute('viewBox')
      ?.split(' ')
      .map(Number) || [0, 0, 2400, 1700]

    rewardSceneSvgDimensions.value = {
      width: rect.width,
      height: rect.height,
      left: rect.left,
      top: rect.top,
      viewBox
    }
  }

  const flipAreas = ref<FlipArea[]>([])

  const initializeFlipAreas = () => {
    if (!flipZoneReference.value) {
      logger.error('Flip zone group not found in SVG')
      return
    }

    const areas = flipZoneReference.value.getElementsByTagName('rect')
    flipAreas.value = Array.from(areas).map((rect) => ({
      id: rect.id,
      x: parseFloat(rect.getAttribute('x') || '0'),
      y: parseFloat(rect.getAttribute('y') || '0'),
      width: parseFloat(rect.getAttribute('width') || '0'),
      height: parseFloat(rect.getAttribute('height') || '0')
    }))
  }

  const isInFlipZone = (x: number, y: number): boolean => {
    if (flipAreas.value.length === 0) {
      logger.warn('Flip areas not initialized')
      return false
    }

    return flipAreas.value.some((area) => {
      const inArea =
        x >= area.x && x <= area.x + area.width && y >= area.y && y <= area.y + area.height

      return inArea
    })
  }

  const convertToScreenCoordinates = (point: AvatarPathPoint): AvatarPathPoint => {
    if (!progressiveMapReference.value) return point

    const ctm = progressiveMapReference.value.getScreenCTM()
    if (!ctm) return point

    const domPoint = new DOMPoint(point.x, point.y)

    const matrix = new DOMMatrix([ctm.a, ctm.b, ctm.c, ctm.d, ctm.e, ctm.f])
    const transformedPoint = domPoint.matrixTransform(matrix)

    const isLandscape = window.innerWidth > window.innerHeight
    let finalX = transformedPoint.x
    let finalY = transformedPoint.y

    if (!isLandscape) {
      finalX = transformedPoint.y
      finalY = transformedPoint.x
    }

    const scrollX = window.pageXOffset || document.documentElement.scrollLeft
    const scrollY = window.pageYOffset || document.documentElement.scrollTop

    return {
      x: finalX + scrollX,
      y: finalY + scrollY,
      inFlipZone: isInFlipZone(point.x, point.y)
    }
  }

  const inProgressiveMapFlipZone = ref<Boolean>(false)

  const setProgressiveMapFlipZone = (state: boolean = false) => {
    inProgressiveMapFlipZone.value = state
  }

  const $reset = () => {
    avatarCurrentPath.value = []
    progressiveMapReference.value = null
    pathSegmentReferences.value = {}
    flipZoneReference.value = null
    inProgressiveMapFlipZone.value = false
  }

  return {
    avatarCurrentPath,
    rewardSceneSvgDimensions,
    inProgressiveMapFlipZone,
    initializeMapComponents,
    updateRewardSceneDimensions,
    getAvatarPathSegment,
    getAvatarPathEndpoint,
    setProgressiveMapFlipZone,
    $reset
  }
})
